import { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormik, useFormikContext } from 'formik';
import { toFormikValidationSchema, toFormikValidate } from 'zod-formik-adapter';
import { AxiosError } from 'axios';
import { Label } from '@antimatterhq/ui/components/label';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import {
  useGetWorkspaceConfig,
  useOptimisticUpdateWorkspaceConfig,
} from '@/hooks/workspace/useGetWorkspaceConfig';
import { useUpdateWorkspaceConfig } from '@/hooks/workspace/useUpdateWorkspaceConfig';
import {
  CustomPresetsSchema,
  CustomPresetsSchemaValues,
} from '@/zod/schemas/workspaceConfig/CustomPresetsSchemaValues';
import {
  getCustomPresetPathInitialValues,
  setCustomPresetPath,
} from '@/lib/workspaceConfig/customPresets';
import { FormInput } from '@/components/form/input/FormInput';
import { FormError } from '@/components/form/form';
import { ResponseError } from '@/components/responseError/ResponseError';

export type CustomPresetsContentProps = {
  responseError?: AxiosError<{ message: string }>;
};

const CustomPresetsContent = ({ responseError }: CustomPresetsContentProps) => {
  const { values, handleChange, handleBlur, isSubmitting } =
    useFormikContext<CustomPresetsSchemaValues>();

  return (
    <>
      <Label htmlFor="daslCustomPresetsPath" id="daslCustomPresetsPath-label">
        Custom presets path
      </Label>
      <FormInput
        name="daslCustomPresetsPath"
        value={values.daslCustomPresetsPath ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isSubmitting}
        placeholder="/Volumes/<catalog>/<schema>/presets"
        aria-labelledby="daslCustomPresetsPath-label"
      />
      <FormError name="daslCustomPresetsPath" />

      <Spacer size="lg" />

      <ResponseError error={responseError} />

      <div className="flex justify-end">
        <Button variant="primary" disabled={isSubmitting} type="submit">
          Submit changes
        </Button>
      </div>
    </>
  );
};

export const CustomPresets = () => {
  const { data: workspaceConfig } = useGetWorkspaceConfig();
  const { mutateAsync: updateConfig } = useUpdateWorkspaceConfig();
  const optimisticUpdate = useOptimisticUpdateWorkspaceConfig();
  const [responseError, setResponseError] = useState<
    AxiosError<{ message: string }> | undefined
  >(undefined);

  const formik = useFormik({
    initialValues: getCustomPresetPathInitialValues(workspaceConfig),
    validationSchema: toFormikValidationSchema(CustomPresetsSchema),
    validate: toFormikValidate(CustomPresetsSchema),
    validateOnChange: true,
    onSubmit: async (newValues) => {
      setResponseError(undefined);
      const config = setCustomPresetPath(workspaceConfig, newValues);

      // save to API
      await updateConfig(config, {
        onSuccess: async () => {
          // reset form
          optimisticUpdate(config);
        },
        onError: async ({ error }) => {
          setResponseError(error as AxiosError<{ message: string }>);
        },
        toasts: {
          success: 'Preset path updated',
          error: 'Failed to update preset path',
        },
      });
    },
  });

  const { setValues } = formik;

  useEffect(() => {
    if (workspaceConfig) {
      setValues(getCustomPresetPathInitialValues(workspaceConfig));
    }
  }, [workspaceConfig, setValues]);

  return (
    <FormikProvider value={formik}>
      <Form>
        <CustomPresetsContent responseError={responseError} />
      </Form>
    </FormikProvider>
  );
};
